/**
 * NotifySignupModal
 *
 * 限定公開期間中のランディングページに出る「公開のお知らせメール登録」 モーダル。
 * メーリングリストという用語は分かりにくいので「公開のお知らせを受け取る」 「リリース通知」
 * という直感的な表現に統一。
 *
 * 表示条件:
 *   - /api/preview/status で previewMode = true
 *   - localStorage に dismiss/registered フラグが無い
 *
 * 動作:
 *   - email 入力 → POST /api/preview/signup → 完了状態に遷移
 *   - 「閉じる」 で localStorage に dismiss flag を保存 (= 二度と自動表示しない、 FAB は残る)
 */
import React, { useState } from 'react';
import {
  Dialog, DialogContent, Box, Typography, TextField, Button, IconButton,
  Alert, CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';

const PRIMARY = '#4f46e5';
const PURPLE_GRAD = 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)';

const STORAGE_DISMISS_KEY = 'penlytics_notify_modal_dismissed';
const STORAGE_REGISTERED_KEY = 'penlytics_notify_registered';

const NotifySignupModal = ({ open, onClose, onRegistered }) => {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (!email.trim()) {
      setError('メールアドレスを入力してください');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch('/api/preview/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), source: 'landing_modal' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || '登録に失敗しました');
      } else {
        setSuccess(true);
        try {
          localStorage.setItem(STORAGE_REGISTERED_KEY, '1');
          localStorage.setItem(STORAGE_DISMISS_KEY, '1');
        } catch {}
        if (onRegistered) onRegistered();
      }
    } catch (err) {
      setError('通信エラーが発生しました');
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    try { localStorage.setItem(STORAGE_DISMISS_KEY, '1'); } catch {}
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
    >
      <Box sx={{
        position: 'relative',
        background: PURPLE_GRAD,
        color: '#fff',
        px: 3, pt: 3.5, pb: 2,
      }}>
        <IconButton
          onClick={handleClose}
          aria-label="閉じる"
          sx={{
            position: 'absolute', top: 8, right: 8,
            color: 'rgba(255,255,255,0.85)',
            '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
          }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box sx={{
          width: 48, height: 48, borderRadius: '12px',
          bgcolor: 'rgba(255,255,255,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          mb: 1.5,
        }}>
          <NotificationsActiveIcon sx={{ fontSize: 28, color: '#fff' }} />
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', letterSpacing: '-0.3px', mb: 0.5 }}>
          公開のお知らせを受け取る
        </Typography>
        <Typography sx={{ fontSize: '0.85rem', opacity: 0.95, lineHeight: 1.6 }}>
          Penlytics は近日公開予定です。
          メールアドレスをご登録いただくと、
          <strong>公開時にお知らせメールをお届け</strong>します。
        </Typography>
      </Box>
      <DialogContent sx={{ px: 3, pt: 3, pb: 3 }}>
        {success ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Box sx={{
              width: 56, height: 56, borderRadius: '50%',
              bgcolor: '#dcfce7',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              mb: 1.5,
            }}>
              <MarkEmailReadIcon sx={{ fontSize: 32, color: '#15803d' }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', mb: 0.5 }}>
              登録ありがとうございます
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: '#536471', lineHeight: 1.7 }}>
              公開時に <strong>{email}</strong> 宛てにお知らせメールをお送りします。
            </Typography>
            <Button
              onClick={handleClose}
              sx={{
                mt: 2.5, py: 1.1, px: 4, borderRadius: '10px',
                textTransform: 'none', fontWeight: 700,
                bgcolor: PRIMARY, color: '#fff',
                '&:hover': { bgcolor: '#4338ca' },
              }}
            >
              閉じる
            </Button>
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: '8px', fontSize: '0.85rem' }}>
                {error}
              </Alert>
            )}
            <TextField
              type="email"
              fullWidth
              autoFocus
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@example.com"
              disabled={busy}
              InputProps={{ sx: { borderRadius: '10px', fontSize: '0.95rem' } }}
              sx={{ mb: 2 }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={busy}
              sx={{
                py: 1.3, borderRadius: '10px',
                textTransform: 'none', fontWeight: 700, fontSize: '0.95rem',
                background: PURPLE_GRAD,
                boxShadow: '0 4px 14px rgba(124,58,237,0.3)',
                '&:hover': { background: PURPLE_GRAD, opacity: 0.92 },
              }}
            >
              {busy ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : '公開のお知らせを受け取る'}
            </Button>
            <Typography sx={{ mt: 2, fontSize: '0.7rem', color: '#94a3b8', textAlign: 'center', lineHeight: 1.7 }}>
              登録メールアドレスは公開通知以外には使用しません。 いつでも解除できます。
            </Typography>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NotifySignupModal;
export { STORAGE_DISMISS_KEY, STORAGE_REGISTERED_KEY };
